import { useForm } from '@inertiajs/react';
import type { FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  LINKEDIN_PROFILE_URL_REGEX,
  normalizeLinkedInProfileUrl,
} from '@/lib/validations/social-profiles';
import type { OnboardingEdition } from '@/types';
import { errorMessages } from './errors';
import {
  FormDescription,
  FormItem,
  FormLabel,
  FormMessage,
  GlobalError,
} from './form-field';
import { OnboardingShell, titleFont } from './OnboardingShell';

const SHIRT_SIZES = ['S', 'M', 'L', 'XL'];

// _components/complete-profile-form.tsx (26-co / 26-mx). Only the fields a draft-promoted
// hacker is actually missing are rendered; everyone else never sees this step.
export function CompleteProfileForm({
  edition,
  needsLinkedin,
  needsShirtSize,
  defaultLinkedin,
}: {
  edition: OnboardingEdition;
  needsLinkedin: boolean;
  needsShirtSize: boolean;
  defaultLinkedin: string;
}) {
  const form = useForm({
    linkedin: needsLinkedin ? '' : defaultLinkedin,
    shirtSize: '',
  });
  const { data, setData, post, processing } = form;
  const errors = errorMessages(form.errors);

  const isValid =
    (!needsLinkedin ||
      LINKEDIN_PROFILE_URL_REGEX.test(
        normalizeLinkedInProfileUrl(data.linkedin.trim()),
      )) &&
    (!needsShirtSize || SHIRT_SIZES.includes(data.shirtSize));

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    post(`${edition.basePath}/complete-profile`, { preserveScroll: true });
  };

  return (
    <OnboardingShell ph26={edition.ph26} width="max-w-2xl">
      {/* Title */}
      <div className="text-center">
        <h1
          className={`inline-block font-bold ${titleFont(edition.ph26)} text-2xl text-primary sm:text-3xl md:text-4xl`}
        >
          Completa tu{' '}
          <span className="bg-primary px-2 py-1 text-background">
            información
          </span>
        </h1>
        <p className="mt-4 font-mono text-primary/70 text-sm">
          Nos faltan algunos datos para tu participación
        </p>
      </div>

      {/* Form Card */}
      <div className="border-2 border-primary bg-background/80 p-6 backdrop-blur-sm sm:p-8 md:p-10">
        <form onSubmit={onSubmit} className="space-y-6">
          {needsLinkedin && (
            <FormItem>
              <FormLabel htmlFor="linkedin" error={!!errors.linkedin}>
                LinkedIn *
              </FormLabel>
              <Input
                id="linkedin"
                type="url"
                placeholder="https://linkedin.com/in/tu-usuario"
                disabled={processing}
                value={data.linkedin}
                onChange={(e) => setData('linkedin', e.target.value)}
              />
              <FormDescription>
                Pega la URL completa de tu perfil de LinkedIn
              </FormDescription>
              <FormMessage>{errors.linkedin}</FormMessage>
            </FormItem>
          )}

          {needsShirtSize && (
            <FormItem>
              <FormLabel htmlFor="shirtSize" error={!!errors.shirtSize}>
                Talla de playera *
              </FormLabel>
              <Select
                value={data.shirtSize}
                onValueChange={(value) => setData('shirtSize', value)}
                disabled={processing}
              >
                <SelectTrigger id="shirtSize">
                  <SelectValue placeholder="Selecciona tu talla" />
                </SelectTrigger>
                <SelectContent>
                  {SHIRT_SIZES.map((size) => (
                    <SelectItem key={size} value={size}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage>{errors.shirtSize}</FormMessage>
            </FormItem>
          )}

          {/* Global Error */}
          <GlobalError>{errors.base}</GlobalError>

          {/* Submit Button */}
          <div className="flex justify-center pt-4">
            <Button
              type="submit"
              size="lg"
              disabled={processing || !isValid}
              className="w-full sm:w-auto"
            >
              {processing ? 'Guardando...' : 'Continuar'}
            </Button>
          </div>
        </form>
      </div>
    </OnboardingShell>
  );
}
